import { useState } from "react";
import api, { MAD, apiError } from "../lib/api";
import { useData } from "../hooks/useData";
import { useAuth } from "../context/AuthContext";
import { usePos } from "../context/PosContext";
import { PageHeader, ExportButtons, StatCard, Loader, Empty } from "../components/ui-kit";
import { Modal, Field } from "../components/Modal";
import { exportExcel, exportPDF } from "../lib/exporters";
import { Plus, Trash2, Pencil, ChefHat, Package, Coins, History, X } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { product_id: "", quantite_produite: "", date: "", heure: "", recette: "", commentaire: "", ingredients: [] };
const emptyLine = () => ({ stock_item_id: "", nom: "", quantite: "", unite: "", cout_unitaire: "" });

export default function Production() {
  const { can } = useAuth();
  const { labPosId, labPosName } = usePos();
  const { data, loading, refetch } = useData("/productions");
  const { data: products } = useData("/products");
  const { data: stockItems } = useData("/stock");

  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [expandedHist, setExpandedHist] = useState({});

  const openNew = () => {
    setEditId(null);
    setForm({ ...EMPTY, product_id: products[0]?.id || "", date: new Date().toISOString().slice(0, 10), ingredients: [] });
    setOpen(true);
  };
  const openEdit = (p) => {
    setEditId(p.id);
    setForm({
      product_id: p.product_id, quantite_produite: p.quantite_produite,
      date: p.date ? p.date.slice(0, 10) : "", heure: p.heure || "",
      recette: p.recette || "", commentaire: p.commentaire || "",
      ingredients: (p.ingredients || []).map((i) => ({
        stock_item_id: i.stock_item_id || "", nom: i.nom || "", quantite: i.quantite ?? "",
        unite: i.unite || "", cout_unitaire: i.cout_unitaire ?? "",
      })),
    });
    setOpen(true);
  };

  // --- Gestion des lignes d'ingrédients ---
  const addLine = () => setForm((f) => ({ ...f, ingredients: [...f.ingredients, emptyLine()] }));
  const removeLine = (idx) => setForm((f) => ({ ...f, ingredients: f.ingredients.filter((_, i) => i !== idx) }));
  const updateLine = (idx, patch) =>
    setForm((f) => ({ ...f, ingredients: f.ingredients.map((l, i) => (i === idx ? { ...l, ...patch } : l)) }));
  const pickStock = (idx, sid) => {
    const it = stockItems.find((s) => s.id === sid);
    updateLine(idx, {
      stock_item_id: sid,
      nom: it ? it.nom : "",
      unite: it ? it.unite : "",
      cout_unitaire: it ? it.cout_unitaire ?? "" : "",
    });
  };

  const lineCost = (l) => (parseFloat(l.quantite) || 0) * (parseFloat(l.cout_unitaire) || 0);
  const totalCost = form.ingredients.reduce((a, l) => a + lineCost(l), 0);
  const qteProd = parseFloat(form.quantite_produite) || 0;
  const costPerUnit = qteProd ? totalCost / qteProd : 0;

  const save = async (e) => {
    e.preventDefault();
    const qte = parseFloat(form.quantite_produite);
    if (!qte || qte <= 0) return toast.error("Indiquez la quantité produite");
    const ingredients = form.ingredients
      .filter((l) => l.nom && (parseFloat(l.quantite) || 0) > 0)
      .map((l) => ({
        stock_item_id: l.stock_item_id || "",
        nom: l.nom,
        quantite: parseFloat(l.quantite) || 0,
        unite: l.unite || "",
        cout_unitaire: l.cout_unitaire === "" || l.cout_unitaire === null ? null : parseFloat(l.cout_unitaire),
      }));
    const payload = {
      quantite_produite: qte,
      ingredients,
      recette: form.recette,
      date: form.date ? new Date(form.date).toISOString() : "",
      heure: form.heure,
      commentaire: form.commentaire,
    };
    try {
      if (editId) {
        await api.put(`/productions/${editId}`, payload);
        toast.success("Production modifiée");
      } else {
        if (!labPosId) return toast.error("Laboratoire introuvable");
        await api.post(`/productions?pos_id=${labPosId}`, { ...payload, product_id: form.product_id });
        toast.success("Production enregistrée (registre — aucun impact stock)");
      }
      setOpen(false); refetch();
    } catch (e2) { toast.error(apiError(e2.response?.data?.detail)); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Supprimer la production ${p.numero_lot} ?`)) return;
    try { await api.delete(`/productions/${p.id}`); toast.success("Supprimée"); refetch(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const fmt = (s) => (s ? new Date(s).toLocaleDateString("fr-FR") : "");
  const totalQte = data.reduce((a, p) => a + (p.quantite_produite || 0), 0);
  const totalCout = data.reduce((a, p) => a + (p.cout_total || 0), 0);

  const cols = [
    { header: "Lot", value: (r) => r.numero_lot },
    { header: "Date", value: (r) => fmt(r.date) },
    { header: "Produit", value: (r) => r.product_nom },
    { header: "Quantité", value: (r) => r.quantite_produite },
    { header: "Coût production (DH)", value: (r) => r.cout_total },
    { header: "Coût / unité (DH)", value: (r) => r.cout_par_produit },
    { header: "Ingrédients", value: (r) => (r.ingredients || []).map((i) => `${i.nom} ${i.quantite}${i.unite}`).join(", ") },
    { header: "Recette", value: (r) => r.recette || "" },
    { header: "Commentaire", value: (r) => r.commentaire || "" },
  ];

  return (
    <div>
      <PageHeader title="Production" subtitle={`${labPosName || "Laboratoire"} — registre des fabrications (sans impact sur le stock).`}>
        {can("production", "export") && <ExportButtons onExcel={() => exportExcel("productions_tiramessi", cols, data)} onPDF={() => exportPDF("Productions TIRAMESSI", cols, data)} />}
        {can("production", "create") && <button className="tm-btn" onClick={openNew} data-testid="add-production-btn"><Plus size={16} /> Nouvelle production</button>}
      </PageHeader>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <StatCard label="Fabrications" value={data.length} icon={ChefHat} />
        <StatCard label="Quantité produite" value={totalQte} icon={Package} />
        <StatCard label="Coût total production" value={MAD(totalCout)} icon={Coins} />
      </div>

      {loading ? <Loader /> : data.length === 0 ? <Empty text="Aucune production enregistrée." /> : (
        <div className="space-y-3" data-testid="productions-list">
          {data.map((p) => (
            <div key={p.id} className="tm-card p-5" data-testid={`production-row-${p.id}`}>
              <div className="flex items-start justify-between flex-wrap gap-3">
                <div>
                  <p className="font-semibold text-lg flex items-center gap-2 flex-wrap">{p.quantite_produite} × {p.product_nom}
                    {p.numero_lot && <span className="tm-badge" style={{ background: "rgba(212,175,55,.15)", color: "var(--tm-gold)" }} data-testid={`lot-${p.id}`}>{p.numero_lot}</span>}
                  </p>
                  <p className="text-xs text-[var(--tm-muted)]">{fmt(p.date)} {p.heure && `· ${p.heure}`} {p.employe && `· ${p.employe}`}</p>
                </div>
                {(can("production", "edit") || can("production", "delete")) && (
                  <div className="flex items-center gap-3">
                    {can("production", "edit") && <button onClick={() => openEdit(p)} className="text-[var(--tm-muted)] hover:text-[var(--tm-gold)]" data-testid={`edit-production-${p.id}`} title="Modifier"><Pencil size={16} /></button>}
                    {can("production", "delete") && <button onClick={() => remove(p)} className="text-[var(--tm-muted)] hover:text-[#F44336]" title="Supprimer"><Trash2 size={16} /></button>}
                  </div>
                )}
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-4">
                <CostCell label="Coût de production" value={MAD(p.cout_total || 0)} gold />
                <CostCell label="Coût / unité" value={MAD(p.cout_par_produit || 0)} />
                <CostCell label="Quantité" value={p.quantite_produite} />
              </div>
              {p.ingredients && p.ingredients.length > 0 && (
                <div className="mt-3">
                  <p className="text-xs uppercase tracking-wider text-[var(--tm-muted)] mb-1.5">Ingrédients ({p.ingredients.length})</p>
                  <div className="flex flex-wrap gap-2" data-testid={`ingredients-${p.id}`}>
                    {p.ingredients.map((ig, ix) => (
                      <span key={ix} className="tm-badge text-xs" style={{ background: "var(--tm-bg)", color: "var(--tm-beige)" }}>
                        {ig.nom} · {ig.quantite}{ig.unite} · {MAD(ig.cout || 0)}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {p.recette && (
                <div className="mt-3">
                  <p className="text-xs uppercase tracking-wider text-[var(--tm-muted)] mb-1">Recette / Notes</p>
                  <p className="text-sm whitespace-pre-wrap" style={{ color: "var(--tm-beige)" }}>{p.recette}</p>
                </div>
              )}
              {p.commentaire && <p className="text-sm text-[var(--tm-muted)] mt-2">{p.commentaire}</p>}
              {p.historique && p.historique.length > 0 && (
                <div className="mt-3 pt-3" style={{ borderTop: "1px solid var(--tm-border)" }}>
                  <button type="button" onClick={() => setExpandedHist((s) => ({ ...s, [p.id]: !s[p.id] }))}
                    className="text-xs flex items-center gap-1.5 text-[var(--tm-muted)] hover:text-[var(--tm-gold)]" data-testid={`hist-toggle-${p.id}`}>
                    <History size={13} /> Historique des modifications ({p.historique.length})
                  </button>
                  {expandedHist[p.id] && (
                    <div className="mt-2 space-y-2" data-testid={`hist-list-${p.id}`}>
                      {[...p.historique].reverse().map((h, hx) => (
                        <div key={hx} className="text-xs tm-card p-2.5" style={{ background: "var(--tm-bg)" }}>
                          <p className="text-[var(--tm-muted)] mb-1">{new Date(h.date).toLocaleString("fr-FR")} · <span style={{ color: "var(--tm-gold)" }}>{h.user || "—"}</span></p>
                          <ul className="list-disc pl-4 space-y-0.5">{h.changements.map((c, cx) => <li key={cx}>{c}</li>)}</ul>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <Modal open={open} onClose={() => setOpen(false)} title={editId ? "Modifier la production" : "Nouvelle production"} testid="production-modal">
        <form onSubmit={save}>
          <Field label="Produit fabriqué">
            <select className="tm-input" value={form.product_id} disabled={!!editId} onChange={(e) => setForm({ ...form, product_id: e.target.value })} data-testid="production-product">
              {products.map((p) => <option key={p.id} value={p.id}>{p.nom}</option>)}
            </select>
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Quantité produite"><input type="number" step="0.01" min="0" className="tm-input" required value={form.quantite_produite} onChange={(e) => setForm({ ...form, quantite_produite: e.target.value })} data-testid="production-qte" /></Field>
            <Field label="Date de production"><input type="date" className="tm-input" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="production-date" /></Field>
          </div>

          {/* Liste dynamique d'ingrédients (calcul du coût, sans impact stock) */}
          <div className="mb-3">
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs uppercase tracking-wider text-[var(--tm-muted)]">Ingrédients (Stock Laboratoire)</label>
              <button type="button" onClick={addLine} className="text-xs flex items-center gap-1 text-[var(--tm-gold)] hover:opacity-80" data-testid="add-ingredient-line">
                <Plus size={14} /> Ajouter un ingrédient
              </button>
            </div>
            {form.ingredients.length === 0 && (
              <p className="text-xs text-[var(--tm-muted)] mb-2">Aucun ingrédient. Ajoutez-en pour calculer le coût de production automatiquement.</p>
            )}
            <div className="space-y-2" data-testid="ingredient-lines">
              {form.ingredients.map((l, idx) => (
                <div key={idx} className="tm-card p-2.5" style={{ background: "var(--tm-bg)" }} data-testid={`ingredient-line-${idx}`}>
                  <div className="flex items-center gap-2">
                    <select className="tm-input flex-1" value={l.stock_item_id} onChange={(e) => pickStock(idx, e.target.value)} data-testid={`ingredient-select-${idx}`}>
                      <option value="">— Choisir un ingrédient —</option>
                      {stockItems.map((s) => <option key={s.id} value={s.id}>{s.nom} ({s.unite})</option>)}
                    </select>
                    <button type="button" onClick={() => removeLine(idx)} className="text-[var(--tm-muted)] hover:text-[#F44336]" data-testid={`remove-ingredient-${idx}`}><X size={16} /></button>
                  </div>
                  <div className="grid grid-cols-3 gap-2 mt-2">
                    <div>
                      <span className="text-[10px] text-[var(--tm-muted)]">Quantité {l.unite && `(${l.unite})`}</span>
                      <input type="number" step="0.001" min="0" className="tm-input" placeholder="Qté" value={l.quantite} onChange={(e) => updateLine(idx, { quantite: e.target.value })} data-testid={`ingredient-qte-${idx}`} />
                    </div>
                    <div>
                      <span className="text-[10px] text-[var(--tm-muted)]">Prix unitaire (DH)</span>
                      <input type="number" step="0.0001" min="0" className="tm-input" placeholder="Prix" value={l.cout_unitaire} onChange={(e) => updateLine(idx, { cout_unitaire: e.target.value })} data-testid={`ingredient-prix-${idx}`} />
                    </div>
                    <div>
                      <span className="text-[10px] text-[var(--tm-muted)]">Coût ligne</span>
                      <p className="tm-input flex items-center tabular" style={{ color: "var(--tm-gold)" }} data-testid={`ingredient-cout-${idx}`}>{MAD(lineCost(l))}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Résumé coût en direct */}
          <div className="tm-card p-3 mb-3 grid grid-cols-2 gap-3" style={{ background: "var(--tm-bg)" }}>
            <div>
              <p className="text-[10px] uppercase tracking-wider text-[var(--tm-muted)]">Coût total production</p>
              <p className="font-semibold tabular mt-0.5" style={{ color: "var(--tm-gold)" }} data-testid="production-total-cout">{MAD(totalCost)}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-wider text-[var(--tm-muted)]">Coût / unité</p>
              <p className="font-semibold tabular mt-0.5" data-testid="production-cout-unite">{MAD(costPerUnit)}</p>
            </div>
          </div>

          <Field label="Recette / Notes (facultatif)">
            <textarea className="tm-input" rows={2} placeholder="Étapes, remarques..." value={form.recette} onChange={(e) => setForm({ ...form, recette: e.target.value })} data-testid="production-recette" />
          </Field>
          <Field label="Commentaires"><input className="tm-input" value={form.commentaire} onChange={(e) => setForm({ ...form, commentaire: e.target.value })} data-testid="production-commentaire" /></Field>
          {!editId && <p className="text-xs text-[var(--tm-muted)] mb-3">Un numéro de lot sera généré automatiquement. Cet enregistrement n'affecte pas le stock laboratoire.</p>}
          <button type="submit" className="tm-btn w-full mt-1" data-testid="production-save">{editId ? "Enregistrer les modifications" : "Valider la production"}</button>
        </form>
      </Modal>
    </div>
  );
}

function CostCell({ label, value, gold, color }) {
  return (
    <div className="tm-card p-3" style={{ background: "var(--tm-bg)" }}>
      <p className="text-[10px] uppercase tracking-wider text-[var(--tm-muted)]">{label}</p>
      <p className="font-semibold tabular mt-0.5" style={{ color: color || (gold ? "var(--tm-gold)" : "var(--tm-beige)") }}>{value}</p>
    </div>
  );
}
